import api from '@/lib/api/axios'

export interface TemplateObject {
    id: number
    ObjectKey: string
    ClassificationID: number
    NamePattern: string
    DefaultDescription?: string | null
    AttachDown: number
    AttachUp: number
    SortOrder: number
    ClassName: string
    ClassCategory: string
    DisplayRank: number
    BackgroundColor?: string | null
    LegalProviderClassIDs?: number[]
}

/** A dependency between two template objects. Each end is exactly one object, so there is nothing to pair. */
export interface TemplateEdge {
    DependentObject: string
    ProviderObject: string
}

export interface Template {
    id: number
    Name: string
    Description?: string | null
    Category: string
    Published?: number
    IsSystem?: number
    objects: TemplateObject[]
    edges: TemplateEdge[]
}

export interface TemplateObjectInput {
    ObjectKey: string
    ClassificationID: number
    NamePattern: string
    DefaultDescription?: string | null
    AttachDown: number
    AttachUp: number
    SortOrder?: number
    MasterRunbookID?: number | null
    SourceObjectID?: string
}

export interface TemplateSaveInput {
    Name: string
    Category?: string
    Description?: string | null
    /** Shipped with the product. Accepted only where template authoring is on. */
    IsSystem?: boolean
    Objects: TemplateObjectInput[]
    Edges: TemplateEdge[]
}

export interface InstanceSpec {
    id: string
    existing?: boolean
}

export interface InstantiateResult {
    template: string
    objects: {
        ObjectID: string
        ObjectKey: string
        ClassificationID: number
        ClassName: string
        DisplayRank: number
        AttachDown: number
        AttachUp: number
    }[]
    mappings: { Scenario: number; SourceID: string; TargetID: string }[]
}

/** A template object as the graphics read needs it: its classification and what it says about itself. */
export interface ObjectGraphicRequest {
    ObjectKey: string
    ClassificationID: number
    DefaultDescription?: string | null
}

/** The graphic an object will draw on the planning canvas: the tag match, or the classification's default. */
export interface ChosenGraphic {
    filename: string
    source: 'match' | 'default'
    tags?: string[]
    width: number | null
    height: number | null
}

export const templatesApi = {
    list: () =>
        api.get<{ data: Template[] }>('/api/templates'),

    // Whether this deployment is where shipped templates are drawn (SHIPPED_CONTENT_AUTHORING_ENABLED).
    authoring: () =>
        api.get<{ data: { enabled: boolean } }>('/api/templates/authoring'),

    // Admin list includes unpublished templates.
    adminList: () =>
        api.get<{ data: Template[] }>('/api/templates?all=1'),

    // Save-as-template from a canvas selection (templates.create).
    save: (payload: TemplateSaveInput) =>
        api.post<{ data: Template }>('/api/templates', payload),

    update: (id: number, payload: Partial<TemplateSaveInput>) =>
        api.put<{ data: Template }>(`/api/templates/${id}`, payload),

    setPublished: (id: number, published: boolean) =>
        api.patch(`/api/templates/${id}/published`, { Published: published }),

    remove: (id: number) =>
        api.delete(`/api/templates/${id}`),

    // What each object will draw once placed, chosen by the planning canvas's own tag matcher from
    // the drafts as they stand. Null for a classification with no graphic assigned.
    objectGraphics: (objects: ObjectGraphicRequest[]) =>
        api.post<{ data: Record<string, ChosenGraphic | null> }>('/api/templates/object-graphics', { Objects: objects }),

    // "Use existing object" picker source, filtered by the template object's classification.
    pickableObjects: (classificationId: number) =>
        api.get<{ data: { ObjectID: string; Description?: string | null }[] }>(
            `/api/templates/pickable-objects?classificationId=${classificationId}`
        ),

    instantiate: (
        templateId: number,
        payload: {
            Prefix: string
            Scenario: number
            Location?: number | null
            Instances?: Record<string, InstanceSpec[]>
        }
    ) =>
        api.post<{ data: InstantiateResult }>(
            `/api/templates/${templateId}/instantiate`,
            payload
        ),
}
